import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip, InputBase, IconButton, Tooltip } from '@mui/material';
import SearchIcon          from '@mui/icons-material/Search';
import CloseIcon           from '@mui/icons-material/Close';
import VisibilityIcon      from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { FILTERS_UPDATE } from '../../actions/siteActions';
import { fitMapToSites }  from '../../hooks/useAutozoom';

const MAX_RESULTS = 50;

// Search + multi-select by Site (CSR). Fully client-side — the ~28k sites are
// already loaded via GetAllSites, same pattern as the ActivityTitleFilter.
// Two display modes (toggle, persisted in filters.siteSearchMode):
//   'highlight' → all sites stay visible, matches get a yellow ring + auto zoom/pan
//   'isolate'   → every non-matching site is hidden from the canvas
const SiteSearchFilter = ({ mapRef, sites }) => {
  const dispatch = useDispatch();
  const filters  = useSelector((s) => s.sites.filters);
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  const selected = filters.selectedSites   || [];
  const mode     = filters.siteSearchMode  || 'highlight';
  const isolating = mode === 'isolate';

  // Unique, sorted CSR list from the currently loaded sites.
  const allCsrs = useMemo(() => {
    const set = new Set();
    (sites || []).forEach((s) => { if (s.site) set.add(s.site); });
    return [...set].sort();
  }, [sites]);

  const results = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return [];
    return allCsrs
      .filter((csr) => csr.toUpperCase().includes(q) && !selected.includes(csr))
      .slice(0, MAX_RESULTS);
  }, [query, allCsrs, selected]);

  const zoomTo = (csrs) => {
    const map = mapRef?.current;
    if (!map || csrs.length === 0) return;
    const matched = (sites || []).filter((s) => csrs.includes(s.site));
    fitMapToSites(map, matched);
  };

  const addSite = (csr) => {
    const next = [...selected, csr];
    dispatch({ type: FILTERS_UPDATE, payload: { selectedSites: next } });
    setQuery('');
    setOpen(false);
    zoomTo(next);
  };

  const removeSite = (csr) => {
    dispatch({ type: FILTERS_UPDATE, payload: { selectedSites: selected.filter((c) => c !== csr) } });
  };

  const clearAll = () => dispatch({ type: FILTERS_UPDATE, payload: { selectedSites: [] } });

  const toggleMode = () => {
    dispatch({ type: FILTERS_UPDATE, payload: { siteSearchMode: isolating ? 'highlight' : 'isolate' } });
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Box ref={ref} sx={{
      position: 'relative',
      backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: 2,
      px: 2, py: 1.5, border: '1px solid #CBD5E0',
      fontFamily: '"IBM Plex Mono", monospace',
      pointerEvents: 'all',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    }}>
      <Typography sx={{ color: '#94A3B8', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.6 }}>
        Site Search
      </Typography>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.6,
        border: '1px solid #CBD5E0', borderRadius: 1.5, px: 1, py: 0.4,
      }}>
        <SearchIcon sx={{ fontSize: 14, color: '#94A3B8', flexShrink: 0 }} />
        <InputBase
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="CSR…"
          sx={{
            flex: 1, fontSize: 11, color: '#334155',
            fontFamily: '"IBM Plex Mono", monospace',
            '& input': { padding: 0 },
            '& input::placeholder': { color: '#CBD5E0', opacity: 1 },
          }}
        />
        {selected.length > 0 && (
          <>
            <Tooltip title={isolating ? 'Isolating selected — click to show all sites again' : 'Showing all sites — click to isolate the selected ones'}>
              <IconButton size="small" onClick={toggleMode} sx={{ p: 0.3 }}>
                {isolating
                  ? <VisibilityIcon sx={{ fontSize: 14, color: '#1565C0' }} />
                  : <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />}
              </IconButton>
            </Tooltip>
            <Chip label={selected.length} size="small" onClick={clearAll}
              sx={{ backgroundColor: '#1565C0', color: '#fff', height: 16, fontSize: 9, cursor: 'pointer', flexShrink: 0, '& .MuiChip-label': { px: 0.6 } }} />
          </>
        )}
      </Box>

      {open && results.length > 0 && (
        <Box sx={{
          position: 'absolute', top: '100%', left: 16, right: 16, mt: 0.5,
          backgroundColor: '#fff', border: '1px solid #CBD5E0', borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: 220, overflowY: 'auto', zIndex: 1400,
        }}>
          {results.map((csr) => (
            <Box key={csr} onClick={() => addSite(csr)}
              sx={{
                px: 1.2, py: 0.5, cursor: 'pointer', borderBottom: '1px solid #EEF2F6',
                '&:hover': { backgroundColor: '#F1F5F9' },
              }}>
              <Typography sx={{ fontSize: 11, color: '#334155', fontFamily: '"IBM Plex Mono", monospace' }}>
                {csr}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {open && query.trim() && results.length === 0 && (
        <Box sx={{
          position: 'absolute', top: '100%', left: 16, right: 16, mt: 0.5,
          backgroundColor: '#fff', border: '1px solid #CBD5E0', borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', px: 1.2, py: 0.8, zIndex: 1400,
        }}>
          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>
            No matching sites
          </Typography>
        </Box>
      )}

      {selected.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.7 }}>
          {selected.map((csr) => (
            <Chip key={csr} label={csr} size="small" onDelete={() => removeSite(csr)}
              deleteIcon={<CloseIcon sx={{ fontSize: 10 }} />}
              sx={{
                backgroundColor: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E0',
                height: 18, fontSize: 9, fontFamily: '"IBM Plex Mono", monospace',
                '& .MuiChip-label': { px: 0.6 },
                '& .MuiChip-deleteIcon': { color: '#94A3B8', ml: 0 },
              }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SiteSearchFilter;